import { Box } from '@mui/material';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import type { UserRole } from '@/types';

import { getUser, isAuthenticated } from '@/data/service/authService';
import type { RouteType } from '@/routes';
import { routes } from '@/routes';

import { ScrollProvider } from '@/context/scrollContext';
import AuthenticatedSnowplow from '../providers/authenticated-snowplow';
import { AmazonConnect } from './AmazonConnect/AmazonConnect';
import { AmazonConnectProvider } from './AmazonConnect/AmazonConnectProvider';
import NavbarV2 from './header/NavBarV2';

type AppRouteType = {
  isPrivate: boolean;
  restrictAccess: UserRole[];
};

type AppRoutesProps = {
  appRoutes?: RouteType[];
};

const AppRoute = ({ isPrivate, restrictAccess }: AppRouteType) => {
  const isAuthorized = isAuthenticated();
  const user = getUser();
  const shouldRestrictAccess = isAuthorized && restrictAccess.includes(user?.role_id);

  if (shouldRestrictAccess) {
    return <Navigate to="/" />;
  }

  return isPrivate && !isAuthorized ? <Navigate to="/" /> : <Outlet />;
};

export const AppRoutesWithoutRouter = ({ appRoutes = routes }: AppRoutesProps) => (
  <Routes>
    {appRoutes.map(({ path, component: Page, navBar, isPrivate, restrictAccess = [], hideAmazonConnect = false }) => {
      return (
        <Route key={path} path="/" element={<AppRoute isPrivate={isPrivate} restrictAccess={restrictAccess} />}>
          <Route
            path={path}
            element={
              <Box>
                {navBar ? (
                  <Box display={'flex'}>
                    <NavbarV2 />
                    <ScrollProvider>
                      <Page />
                      <AmazonConnect isEnabled={!hideAmazonConnect} />
                    </ScrollProvider>
                  </Box>
                ) : (
                  <Page />
                )}
              </Box>
            }
          />
        </Route>
      );
    })}
  </Routes>
);

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <AuthenticatedSnowplow>
        <AmazonConnectProvider>
          <AppRoutesWithoutRouter />
        </AmazonConnectProvider>
      </AuthenticatedSnowplow>
    </BrowserRouter>
  );
};

export default AppRoutes;
